import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { EmployeeAvatar } from '../../../shared/components';
import styles from './ConversationAssigneePicker.module.css';

export interface ConversationAssigneeOption {
  id: string;
  name: string;
}

interface ConversationAssigneePickerProps {
  employeeOptions: ConversationAssigneeOption[];
  assignedToEmployeeId?: string;
  assignedToEmployeeName?: string;
  isUpdating?: boolean;
  onAssign: (employeeId: string) => void;
  onUnassign: () => void;
}

const POPOVER_WIDTH = 280;
const POPOVER_GAP = 8;

const buildPopoverStyle = (trigger: HTMLElement): React.CSSProperties => {
  const rect = trigger.getBoundingClientRect();
  const width = Math.min(POPOVER_WIDTH, window.innerWidth - 32);
  const left = Math.min(
    Math.max(16, rect.left),
    window.innerWidth - width - 16,
  );
  const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP;
  const spaceAbove = rect.top - POPOVER_GAP;
  const openUpward = spaceBelow < 280 && spaceAbove > spaceBelow;

  return {
    position: 'fixed',
    top: openUpward ? undefined : rect.bottom + POPOVER_GAP,
    bottom: openUpward ? window.innerHeight - rect.top + POPOVER_GAP : undefined,
    left,
    width,
    zIndex: 1200,
  };
};

const popoverStylesEqual = (left: React.CSSProperties, right: React.CSSProperties): boolean =>
  left.top === right.top
  && left.bottom === right.bottom
  && left.left === right.left
  && left.width === right.width;

export const ConversationAssigneePicker: React.FC<ConversationAssigneePickerProps> = ({
  employeeOptions,
  assignedToEmployeeId,
  assignedToEmployeeName,
  isUpdating = false,
  onAssign,
  onUnassign,
}) => {
  const { t } = useTranslation('conversations');
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [popoverStyle, setPopoverStyle] = React.useState<React.CSSProperties | null>(null);

  const searchQuery = search.trim().toLocaleLowerCase();
  const filteredEmployees = React.useMemo(
    () => employeeOptions.filter(employee => (
      !searchQuery || employee.name.toLocaleLowerCase().includes(searchQuery)
    )),
    [employeeOptions, searchQuery],
  );

  const closePicker = React.useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const updatePopoverPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const nextStyle = buildPopoverStyle(trigger);
    setPopoverStyle(current => (current && popoverStylesEqual(current, nextStyle) ? current : nextStyle));
  }, []);

  const openPicker = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    setPopoverStyle(buildPopoverStyle(trigger));
    setIsOpen(true);
  }, []);

  const togglePicker = React.useCallback(() => {
    if (isUpdating) {
      return;
    }

    if (isOpen) {
      closePicker();
      return;
    }

    openPicker();
  }, [closePicker, isOpen, isUpdating, openPicker]);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePopoverPosition();

    const focusFrame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
    };
  }, [isOpen, updatePopoverPosition]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }

      closePicker();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePicker();
      }
    };

    const handleReposition = () => updatePopoverPosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [closePicker, isOpen, updatePopoverPosition]);

  const displayName = isUpdating
    ? t('assigning')
    : (assignedToEmployeeName?.trim() || t('unassigned'));

  const hasAssignedEmployee = Boolean(assignedToEmployeeId && assignedToEmployeeId !== '__pending__');

  const popover = isOpen && popoverStyle ? (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={popoverStyle}
      role="listbox"
      aria-label={t('assignConversation')}
    >
      <input
        ref={searchInputRef}
        className={styles.search}
        type="search"
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder={t('searchEmployeesToAssign')}
        aria-label={t('searchEmployeesToAssign')}
      />

      <div className={styles.list}>
        {filteredEmployees.length === 0 ? (
          <div className={styles.empty}>{t('noEmployeesFound')}</div>
        ) : filteredEmployees.map(employee => {
          const isCurrentAssignee = assignedToEmployeeId === employee.id;

          return (
            <button
              key={employee.id}
              type="button"
              role="option"
              aria-selected={isCurrentAssignee}
              className={`${styles.option} ${isCurrentAssignee ? styles.optionActive : ''}`}
              disabled={isCurrentAssignee || isUpdating}
              onClick={() => {
                onAssign(employee.id);
                closePicker();
              }}
            >
              <EmployeeAvatar name={employee.name} size={24} />
              <span className={styles.optionName}>{employee.name}</span>
              {isCurrentAssignee ? <span className={styles.check} aria-hidden="true">✓</span> : null}
            </button>
          );
        })}
      </div>

      {hasAssignedEmployee ? (
        <button
          type="button"
          className={styles.unassignButton}
          onClick={() => {
            onUnassign();
            closePicker();
          }}
          disabled={isUpdating}
        >
          {t('unassign')}
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={togglePicker}
        disabled={isUpdating}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t('assignConversation')}
      >
        <span className={styles.triggerLabel}>{t('assigneeLabel')}</span>
        <span className={styles.triggerValue}>{displayName}</span>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} aria-hidden="true">▾</span>
      </button>

      {popover ? createPortal(popover, document.body) : null}
    </div>
  );
};
